import React, { useEffect, useRef, useState } from "react";
import { Loader2, Image, Sparkles } from "lucide-react";
import { withBackend } from "../utils/api";
import { useLazyThumbnail } from "../hooks/useLazyThumbnail";

const LazyThumbnail = ({
  thumbnailUrl,
  lazyThumbnailUrl,
  slideNumber,
  className = "",
  onError,
  slide, // NEW: Pass full slide object for client-side generation
  projectId, // NEW: Pass project ID for caching
  fileHash, // NEW: File hash for on-demand backend generation
  isLargeFile = false // NEW: Flag to show "Generate Preview" button
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [manualGenerationRequested, setManualGenerationRequested] = useState(false);
  const requestedUrlRef = useRef(null);

  // DISABLED: Client-side Canvas generation replaced with backend LibreOffice generation
  // All thumbnails now come from backend via thumbnail_url or on-demand /thumbnail endpoint
  const shouldGenerate = false; // Never use client-side generation anymore
  const {
    thumbnail: generatedThumbnail,
    loading: generating,
    error: generationError
  } = useLazyThumbnail(slide, projectId, shouldGenerate);

  useEffect(() => {
    // Handle backend-generated thumbnails (data: URIs or http URLs)
    // Empty string '' means no thumbnail (will show Generate Preview button)
    if (thumbnailUrl && thumbnailUrl.trim() !== '') {
      if (thumbnailUrl.startsWith("data:") || thumbnailUrl.startsWith("http")) {
        setImageUrl(thumbnailUrl);
      } else if (thumbnailUrl.startsWith("/thumbnail/")) {
        // Backend lazy thumbnail URL - trigger fetch
        setImageUrl(null); // Will show Generate Preview button for large files
      } else {
        setImageUrl(null);
      }
    } else {
      // No thumbnail or empty string - show Generate Preview button
      setImageUrl(null);
    }
  }, [thumbnailUrl]);

  // Handle generated thumbnail from hook
  useEffect(() => {
    if (generatedThumbnail) {
      setImageUrl(generatedThumbnail);
      setLoading(false);
      setError(false);
    }
  }, [generatedThumbnail]);

  // Handle generation errors
  useEffect(() => {
    if (generationError) {
      setError(true);
      setLoading(false);
      if (onError) {
        onError(generationError);
      }
    }
  }, [generationError, onError]);

  // Handle generating state
  useEffect(() => {
    setLoading(generating);
  }, [generating]);

  // Handle manual thumbnail generation for large files
  const handleGeneratePreview = async () => {
    if (!fileHash || !slideNumber) return;

    setManualGenerationRequested(true);
    setLoading(true);
    setError(false);

    try {
      console.log(`🎨 Requesting on-demand thumbnail for slide ${slideNumber}...`);
      const response = await fetch(withBackend(`/thumbnail/${fileHash}/${slideNumber}`));

      if (!response.ok) {
        throw new Error(`Failed to generate thumbnail: ${response.status}`);
      }

      const data = await response.json();

      if (data.thumbnail) {
        console.log(`✅ On-demand thumbnail generated for slide ${slideNumber}`);
        setImageUrl(data.thumbnail);
        setError(false);
      } else {
        throw new Error('No thumbnail data received');
      }
    } catch (err) {
      console.error(`❌ Error generating thumbnail for slide ${slideNumber}:`, err);
      setError(true);
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !imageUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-1" />
          <div className="text-xs text-gray-500">Generating...</div>
        </div>
      </div>
    );
  }

  // Show "Generate Preview" button for large files without thumbnails
  if (!imageUrl && isLargeFile && !manualGenerationRequested && fileHash) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <button
          onClick={handleGeneratePreview}
          className="flex flex-col items-center gap-2 px-4 py-3 text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-lg transition-colors group"
        >
          <Sparkles className="w-5 h-5 group-hover:text-indigo-600" />
          <span className="text-xs font-medium">Generate Preview</span>
        </button>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400`}>
        <div className="text-center">
          <Image className="w-6 h-6 mx-auto mb-1" />
          <div className="text-xs">Slide {slideNumber}</div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`Slide ${slideNumber}`}
      className={className}
      onError={(e) => {
        console.error(`Thumbnail display error for slide ${slideNumber}`);
        setError(true);
        if (onError) {
          onError(e);
        }
      }}
    />
  );
};

export default LazyThumbnail;




